/**
 * Orden típico de eventos Meta (landing → cualificado en /qualified):
 * 1. PageView          — index.html al cargar el sitio
 * 2. FunnelHomeView    — primera vista home
 * 3. Lead              — envío del formulario de acceso al video
 * 4. ViewContent       — primera reproducción del video de ventas
 * 5. ProceedToQualificationForm — click hacia /form
 * 6. QualificationFormStep      — por cada paso del formulario
 * 7. InvestmentStepViewed       — al entrar al paso precio
 * 8. QualificationSegmentAssigned — tras respuesta OK del API
 * 9. CompleteRegistration        — al montar /qualified (solo leads cualificados)
 *
 * Lead y CompleteRegistration se mandan además por la Conversions API desde el
 * backend, con el mismo eventID, para no perder conversiones por bloqueadores o iOS.
 */

function fbqReady() {
  return typeof window !== 'undefined' && typeof window.fbq === 'function';
}

function trackCustom(name, params = {}) {
  if (!fbqReady()) return;
  window.fbq('trackCustom', name, params);
}

/**
 * Evento estándar de conversión. Se llama al montar /qualified.
 * @param {string} eventId - ID único para deduplicar con el CAPI del servidor
 */
export function trackCompleteRegistration(eventId) {
  if (!fbqReady()) return;
  window.fbq(
    'track',
    'CompleteRegistration',
    { content_name: 'qualification_form_qualified' },
    { eventID: eventId }
  );
}

/**
 * El lead confirmó que agendó su llamada en /qualified. Es lo más cerca de la
 * venta que se puede medir en el sitio: el pago ocurre en Flow, fuera de acá.
 * @param {string} eventId - Mismo ID que el CAPI, para deduplicar
 */
export function trackScheduleCall(eventId) {
  if (!fbqReady()) return;
  window.fbq(
    'track',
    'Schedule',
    { content_name: 'llamada_agendada' },
    eventId ? { eventID: eventId } : undefined
  );
}

/** Llegada a la landing (home). */
export function trackFunnelHomeView({ hasVideoLeadSaved }) {
  trackCustom('FunnelHomeView', {
    has_video_lead_saved: hasVideoLeadSaved ? 'true' : 'false'
  });
}

/**
 * content_name por origen del lead. Sin esto los 4 formularios (VSL, calculadora,
 * split y guía de sueño) llegaban a Meta con la misma etiqueta y no se podía saber
 * qué lead magnet convertía.
 * Debe coincidir con SOURCE_MAP en backend/src/controllers/videoLeadController.js.
 */
export const CONTENT_NAME_POR_SOURCE = {
  'calorie-macro-calculator': 'calorie_macro_calculator',
  '5-day-split': 'split_5_dias',
  'sleep-guide': 'guia_sueno',
  'blood-work': 'guia_examenes_sangre',
};
export const CONTENT_NAME_DEFAULT = 'video_access_form';

export function contentNameForSource(source) {
  return CONTENT_NAME_POR_SOURCE[source] || CONTENT_NAME_DEFAULT;
}

/**
 * Evento Lead. El `eventId` tiene que ser el mismo que se manda al backend para
 * el CAPI: así Meta cuenta una sola conversión aunque lleguen los dos eventos.
 */
export function trackVideoAccessLead(eventId, source) {
  if (!fbqReady()) return;
  window.fbq(
    'track',
    'Lead',
    { content_name: contentNameForSource(source) },
    eventId ? { eventID: eventId } : undefined
  );
}

/**
 * PageView manual para la navegación interna de la SPA. El snippet de index.html
 * solo dispara el PageView de la carga inicial, así que /form, /qualified y las
 * páginas de recursos no generaban ninguno: las audiencias por URL visitada
 * quedaban vacías.
 */
export function trackPageView() {
  if (!fbqReady()) return;
  window.fbq('track', 'PageView');
}

export function trackSalesVideoPlay() {
  if (!fbqReady()) return;
  window.fbq('track', 'ViewContent', { content_name: 'sales_video' });
}

/**
 * Click en "Completa el formulario" desde la home.
 * `video_minute` puede ser null si no hubo reproductor.
 */
export function trackProceedToQualificationForm({ videoMinute, hadPlayer }) {
  trackCustom('ProceedToQualificationForm', {
    video_minute:
      videoMinute == null || Number.isNaN(videoMinute) ? '' : String(videoMinute),
    had_player: hadPlayer ? 'true' : 'false'
  });
}

const STEP_KEYS = ['contact', 'qualification', 'price'];

/** Vista de cada paso del formulario (una vez por paso y sesión en la página). */
export function trackQualificationFormStep(stepIndex) {
  const stepKey = STEP_KEYS[stepIndex] ?? String(stepIndex);
  trackCustom('QualificationFormStep', {
    step_index: String(stepIndex),
    step: stepKey
  });
  if (stepIndex === 2) {
    trackInvestmentStepViewed();
  }
}

export function trackInvestmentStepViewed() {
  if (!fbqReady()) return;
  window.fbq('trackCustom', 'InvestmentStepViewed');
}

/** Cierra/sale del formulario sin enviar (pagehide). */
export function trackQualificationFormAbandoned({ lastStepIndex }) {
  trackCustom('QualificationFormAbandoned', {
    last_step_index: String(lastStepIndex),
    last_step: STEP_KEYS[lastStepIndex] ?? String(lastStepIndex)
  });
}

export function trackQualificationSubmittedNotQualified({ segment } = {}) {
  if (!fbqReady()) return;
  window.fbq('trackCustom', 'QualificationSubmittedNotQualified', {
    lead_segment: segment || ''
  });
}

export function trackQualificationSegmentAssigned({ segment, score } = {}) {
  if (!fbqReady()) return;
  window.fbq('trackCustom', 'QualificationSegmentAssigned', {
    lead_segment: segment || '',
    score: score == null ? '' : String(score)
  });
}
